import type { Signal } from '@angular/core';
import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type { AdvancedBorderSettings } from '@components/sp-advanced-border-settings/type/advanced-border-settings.type';
import type { AdvancedRadiusSettings } from '@components/sp-advanced-radius-settings/type/sp-advanced-radius-settings.type';
import { type SelectedFileExtended } from '@common/types/file.type';

import {
    atDevice,
    BACKGROUND_LAYER_SEEDS,
    backgroundAtDevice,
    columnWidth,
    columnWidthUnit,
    convertColumnWidths,
    CORNERS,
    createBorderSide,
    DEVICES,
    isBorderIndependent,
    isBorderRadiusIndependent,
    sectionColumnGap,
    sectionIsFlexibleWidths,
    SIDES,
    toVerticalPosition,
    VERTICAL_ALIGN_BY_POSITION,
} from '@sites/entity-core';
import type { VerticalPosition } from '@sites/entity-core';

import type { NormalizedColumnEntity, NormalizedDefaultColumnEntity, NormalizedSectionEntity } from '@web/types';

import { CSS_RESTRICTIONS } from '@common/constants';
import { DeviceMode } from '@common/types/layout.type';
import { WidgetDataSubTypes, Widgets, WidgetsDataTypes } from '@common/enums';
import { ERadioGroupType } from '@ui/sp-radio-group';

import type {
    ColumnBorderSide,
    ColumnCornersValue,
    ColumnDefaultForm,
    ColumnDefaultProps,
    ColumnSpacingValue,
    ColumnWidthUnit,
    SectionDefaultForm,
} from '@sites/entity-signal-form';

import { WebColumnFormService } from '@web/core/services/web-column-form.service';
import { WebSectionFormService } from '@web/core/services/web-section-form.service';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { ImageEditorService } from '@web/shared/modals/image-editor-modal/image-editor.service';

import { AbstractBaseSignalSettingsComponent } from '../abstract-base-settings/abstract-base-signal-settings.component';
import { environment } from '@web-env/environment';
import { UseUnsplash } from '@web/shared/hooks/use-unsplash.hook';
import { SettingsBodyComponent } from '../settings-body/settings-body.component';
import { SettingsBreadcrumbsComponent } from '../settings-breadcrumbs/settings-breadcrumbs.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { SpSwitchComponent } from '@libs/ui/sp-switch/src/sp-switch.component';
import { SpInputNumComponent } from '@libs/components/sp-form-controls/sp-input-num/sp-input-num.component';
import { SpRadioGroupComponent } from '@libs/ui/sp-radio-group/src/sp-radio-group.component';
import { WebDeviceToggleComponent } from '@web/shared/components/web-device-toggle/web-device-toggle.component';
import { WebSynchronizeStyleToggleComponent } from '@web/shared/components/web-synchronize-style-toggle';
import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';
import { SpAdvancedRadiusSettingsComponent } from '@libs/components/sp-advanced-radius-settings/sp-advanced-radius-settings.component';
import { WebBackgroundImageSettingsComponent } from '@web/shared/components/web-background-image-settings/web-background-image-settings.component';
import { SpAdvancedBorderSettingsComponent } from '@libs/components/sp-advanced-border-settings/sp-advanced-border-settings.component';
import { SpAnimationSettingsSignalComponent } from '@components/sp-animation-settings-signal';
import { TranslateModule } from '@ngx-translate/core';
import { SpShadowSettingsSignalComponent } from '@components/sp-shadow-settings-signal';
import type { BackgroundAtDevice, Device, Responsive, Side } from '@sites/entity-core';

const DEVICE_HINT: Record<Device, string> = {
    desktop: 'desktop_device_hint',
    tablet: 'tablet_device_hint',
    mobile: 'mobile_device_hint',
};

/**
 * The shared radius, border and background panels are used by blocks and grids
 * too, so they still speak the legacy CSS key names. A v0 column stores four
 * corners, four sides and one background node, and the two vocabularies are
 * translated here — at the boundary — rather than in the entity.
 */
const RADIUS_KEY = {
    topLeft: 'borderTopLeftRadius',
    topRight: 'borderTopRightRadius',
    bottomRight: 'borderBottomRightRadius',
    bottomLeft: 'borderBottomLeftRadius',
} as const;

const SIDE_KEY = {
    top: ['borderTopWidth', 'borderTopStyle', 'borderTopColor'],
    right: ['borderRightWidth', 'borderRightStyle', 'borderRightColor'],
    bottom: ['borderBottomWidth', 'borderBottomStyle', 'borderBottomColor'],
    left: ['borderLeftWidth', 'borderLeftStyle', 'borderLeftColor'],
} as const;

const at = <T>(responsive: Responsive<T> | undefined, device: Device): T => atDevice(responsive, device) as T;

const toRadiusSettings = (corners: ColumnCornersValue): AdvancedRadiusSettings =>
    Object.fromEntries(CORNERS.map((corner) => [RADIUS_KEY[corner], corners?.[corner]])) as AdvancedRadiusSettings;

const fromRadiusSettings = (settings: AdvancedRadiusSettings): ColumnCornersValue =>
    Object.fromEntries(
        CORNERS.map((corner) => [corner, Number((settings as Record<string, unknown>)[RADIUS_KEY[corner]] ?? 0)])
    ) as ColumnCornersValue;

const fromBorderSettings = (settings: AdvancedBorderSettings, side: Side): ColumnBorderSide => {
    const [widthKey, styleKey, colorKey] = SIDE_KEY[side];
    const bag = settings as unknown as Record<string, unknown>;

    return {
        width: Number(bag[widthKey] ?? 0),
        style: String(bag[styleKey] ?? ''),
        color: String(bag[colorKey] ?? ''),
    };
};

/**
 * Settings for one column. Reads come off the column signal form and writes go
 * straight back into it — `WebColumnFormService` owns the dispatch and the
 * history entry, and `WebSectionFormService` the two row-level knobs.
 *
 * **Everything below is written once, for the breakpoint the device tabs are
 * showing.** The three tabs edit the same properties, so the panel reads through
 * {@link device} and writes through {@link write}; only the fan-out differs, and
 * that is what `syncWithDesktop` decides.
 */
@Component({
    selector: 'web-column-settings',
    templateUrl: './column-settings.component.html',
    styleUrls: ['./column-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SettingsBodyComponent,
        SettingsBreadcrumbsComponent,
        TooltipModule,
        SpSwitchComponent,
        SpInputNumComponent,
        SpRadioGroupComponent,
        WebDeviceToggleComponent,
        WebSynchronizeStyleToggleComponent,
        WebColorPickerComponent,
        SpAdvancedRadiusSettingsComponent,
        WebBackgroundImageSettingsComponent,
        SpAdvancedBorderSettingsComponent,
        SpAnimationSettingsSignalComponent,
        SpShadowSettingsSignalComponent,
        TranslateModule,
    ],
})
export class ColumnSettingsComponent extends AbstractBaseSignalSettingsComponent implements OnInit {
    public readonly entity = input.required<NormalizedColumnEntity>();
    public readonly parentEntity = input.required<NormalizedSectionEntity>();

    private readonly siteFacade = inject(SiteFacade);
    private readonly useUnsplash = inject(UseUnsplash);
    private readonly widgetsFacade = inject(WidgetsFacade);
    private readonly columnsFacade = inject(ColumnsFacade);
    private readonly imageEditorService = inject(ImageEditorService);
    private readonly sectionFormService = inject(WebSectionFormService);
    private readonly columnFormService = inject(WebColumnFormService);
    private readonly destroyRef = inject(DestroyRef);

    protected form!: ColumnDefaultForm;
    private parentForm!: SectionDefaultForm;

    private readonly widgetsEntities = toSignal(this.widgetsFacade.widgetsEntities$, { initialValue: {} });
    private readonly columnsEntities = toSignal(this.columnsFacade.columnsEntities$, { initialValue: {} });

    public readonly radioGroupType = ERadioGroupType;

    /** Which breakpoint the panel's device tabs are showing. */
    public readonly device = toSignal(this.layoutFacade.deviceMode$, {
        initialValue: DeviceMode.desktop,
    }) as Signal<Device>;

    public readonly deviceHint = computed<string>(() => DEVICE_HINT[this.device()]);

    public ngOnInit(): void {
        this.form = this.columnFormService.getOrCreateScoped(this.entity().id, this.destroyRef) as ColumnDefaultForm;
        this.parentForm = this.sectionFormService.getOrCreateScoped(
            this.parentEntity().id,
            this.destroyRef
        ) as SectionDefaultForm;
    }

    // --- Reads ----------------------------------------------------------------

    /**
     * The panel is only reachable for an ordinary column — a header slot has no
     * settings entry and a zero column is not selectable — so the subtype is
     * narrowed once here rather than at every reader below. Live values come off
     * the form; the input is the fallback until it is acquired.
     */
    private readonly props = computed<ColumnDefaultProps | undefined>(
        () => (this.form.value()?.props ?? this.entity()?.props) as ColumnDefaultProps | undefined
    );

    private readonly sectionProps = computed<NormalizedSectionEntity['props'] | undefined>(
        () => this.parentForm.value()?.props ?? this.parentEntity()?.props
    );

    private readonly storedBackground = computed(() => this.props()?.background);

    // --- Row-level knobs ---

    /** Both row knobs only make sense once the row has something to space out. */
    public readonly hasSiblingColumns = computed<boolean>(() => (this.parentEntity()?.children.length ?? 0) > 1);

    // The accessors narrow with `in`, which throws on an absent `props` — and the
    // row is absent for the tick between the panel opening and its form arriving.
    public readonly isFlexibleWidths = computed<boolean>(() => {
        const props = this.sectionProps();

        return props ? sectionIsFlexibleWidths({ props } as NormalizedSectionEntity) : false;
    });

    public readonly columnGap = computed<number>(() => {
        const props = this.sectionProps();

        return props ? sectionColumnGap({ props } as NormalizedSectionEntity) : 0;
    });

    // --- Column-level knobs, none of them per-breakpoint ---

    /** In the letters `sp-radio-group` speaks; the column stores the words. */
    public readonly verticalAlign = computed<string | null>(() => toVerticalPosition(this.props()?.verticalAlign));
    public readonly syncWithDesktop = computed<boolean>(() => Boolean(this.props()?.syncWithDesktop));
    public readonly isShadow = computed<boolean>(() => Boolean(this.props()?.shadow?.isEnabled));

    public readonly hasProductOrCatalogWidget = computed<boolean>(() => {
        const widgets = this.widgetsEntities();

        return Boolean(
            this.entity()?.children?.some(
                ({ id }) => widgets[id]?.name === Widgets.PRODUCT_CARD || widgets[id]?.name === Widgets.CATALOG_PRODUCTS
            )
        );
    });

    // --- Per-breakpoint knobs, all read for the tab being shown ---

    public readonly padding = computed<ColumnSpacingValue>(() => at(this.props()?.padding, this.device())?.value);

    private readonly corners = computed<ColumnCornersValue>(
        () => at(this.props()?.borderRadius?.corners, this.device())?.value
    );

    public readonly borderRadius = computed<number>(() => this.corners()?.topLeft ?? 0);
    public readonly radiusSettings = computed<AdvancedRadiusSettings>(() => toRadiusSettings(this.corners()));

    /**
     * Derived from the corners, with the switch's own answer winning while the
     * panel is open — see `resolveIndependentCorners`.
     */
    public readonly isIndependentCorners = computed<boolean>(() =>
        this.resolveIndependentCorners(
            this.device(),
            isBorderRadiusIndependent(this.props()?.borderRadius, this.device())
        )
    );

    public readonly isBorder = computed<boolean>(() => Boolean(at(this.props()?.border?.isEnabled, this.device())));

    public readonly borderSettings = computed<AdvancedBorderSettings>(() => this.borderSettingsFor(this.device()));

    public readonly backgroundColor = computed<string>(() => at(this.storedBackground()?.color, this.device()) ?? '');

    /** What the image picker edits. Its scroll effect is the column's own flat feature. */
    public readonly background = computed<BackgroundAtDevice>(() => {
        const scrollEffect = this.props()?.scrollEffect;

        return {
            ...backgroundAtDevice(this.storedBackground(), this.device()),
            scrollEffect: {
                isEnabled: Boolean(scrollEffect?.isEnabled),
                value: scrollEffect?.value ?? BACKGROUND_LAYER_SEEDS.scrollEffect.value,
            },
        };
    });

    public readonly backgroundImage = computed(() => this.background().image);

    // --- Writes ---------------------------------------------------------------

    /**
     * Runs a write for every breakpoint it should land on, in one transaction —
     * a single update and a single history entry.
     *
     * With styles synchronised all three tabs show the same settings, so an edit
     * goes to all three; otherwise only the tab being edited changes.
     */
    private write(apply: (device: Device) => void): void {
        const devices = this.syncWithDesktop() ? DEVICES : [this.device()];

        this.form.transact(() => {
            devices.forEach(apply);
            this.form.compact();
        });
    }

    // --- Row-level writes ---

    public setColumnGap(value: number): void {
        this.parentForm.setColumnGap(value);
    }

    public setIsFlexibleWidths(value: boolean): void {
        this.parentForm.setIsFlexibleWidths(value);
        this.convertRowWidths(value ? '%' : 'col');
    }

    /**
     * The row owns the unit and each column stores one number in it, so flipping
     * the mode has to carry every width across — there is no sibling field
     * holding the other unit any more.
     *
     * The whole row is converted at once: the twelve-unit grid is coarse, so
     * `convertColumnWidths` rounds the columns as a set and hands the leftover
     * twelfth to the widest one rather than leaving the row short. Each width is
     * written through that column's own form, so the conversion joins the same
     * history batch as the switch that triggered it.
     */
    private convertRowWidths(unit: ColumnWidthUnit): void {
        const entities = this.columnsEntities();

        // A row with a width mode only ever holds ordinary columns.
        const columns = (this.parentEntity()?.children ?? [])
            .map((id) => entities[id as string])
            .filter((column): column is NormalizedDefaultColumnEntity => column?.entitySubType === 'default');

        if (!columns.length || columnWidthUnit(columns[0]) === unit) {
            return;
        }

        const widths = convertColumnWidths(
            columns.map((column) => columnWidth(column)),
            unit
        );

        columns.forEach((column, index) => {
            // Scoped to the panel: a sibling's form has to outlive this call, since
            // the form dispatches its update from an effect rather than inline.
            const form = this.columnFormService.getOrCreateScoped(column.id, this.destroyRef) as ColumnDefaultForm;

            form.setWidthWithUnit(widths[index], unit);
        });
    }

    // --- Column-level writes ---

    public setVerticalAlign(value: string): void {
        this.form.setVerticalAlign(VERTICAL_ALIGN_BY_POSITION[value as VerticalPosition] ?? null);
    }

    public setShadow(isEnabled: boolean): void {
        this.form.setShadow({ isEnabled });
    }

    /**
     * Copies every desktop style onto both device tabs and flips the flag. One
     * transaction, so it is a single undo step.
     */
    public setSynchronizeStyle(): void {
        const form = this.form;
        const syncWithDesktop = !this.syncWithDesktop();
        const props = this.props();
        const border = this.borderSettingsFor('desktop');

        form.transact(() => {
            (['tablet', 'mobile'] as const).forEach((device) => {
                form.setPadding(props?.padding?.desktop?.value, device);
                // Nothing to copy for the independent-corners switch: the four
                // corners carry the answer, and they are copied right above.
                form.setRadius(props?.borderRadius?.corners?.desktop?.value, device);
                this.writeBorderSettings(border, device);
                form.setBackgroundColor(props?.background?.color?.desktop, device);
                // Every layer, not only the image — see the block panel's copy.
                form.setBackgroundAtDevice(backgroundAtDevice(props?.background, 'desktop'), device);
            });

            form.setSyncWithDesktop(syncWithDesktop);
            // With synchronisation on, the copies above repeat desktop and are dropped.
            form.compact();
        });
    }

    // --- Per-breakpoint writes ---

    public setBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.write((device) => this.form.setBackgroundColor(presetName ?? value ?? null, device));
    }

    public setPadding(side: Side, value: number): void {
        this.write((device) => this.form.setPadding({ [side]: value }, device));
    }

    public setAllPaddings(value: number): void {
        this.write((device) => this.form.setPadding({ top: value, right: value, bottom: value, left: value }, device));
    }

    public setBorderRadius(value: number): void {
        this.write((device) =>
            this.form.setRadius({ topLeft: value, topRight: value, bottomRight: value, bottomLeft: value }, device)
        );
    }

    public setAdvancedRadiusSettings(settings: AdvancedRadiusSettings): void {
        this.write((device) => this.form.setRadius(fromRadiusSettings(settings), device));
    }

    /** Leaving the mixed-corners mode flattens the four corners back onto one. */
    public toggleIndependentCorners(): void {
        const isIndependent = !this.isIndependentCorners();
        const radius = this.borderRadius();

        this.form.transact(() => {
            this.setBorderRadius(radius);
            this.write((device) => this.rememberIndependentCorners(device, isIndependent));
        });
    }

    public setBorder(isEnabled: boolean): void {
        this.write((device) => this.form.setBorderEnabled(isEnabled, device));
    }

    public setAdvancedBorderSettings(settings: AdvancedBorderSettings): void {
        this.write((device) => this.writeBorderSettings(settings, device));
    }

    public setBackground(background: BackgroundAtDevice): void {
        const { image } = background;
        const picked = image ? { ...image, hasResize: true } : image;

        this.write((device) => {
            this.form.setBackgroundAtDevice({ ...background, image: picked }, device);
            // Not per-device — the renderer only ever read the desktop one, so the
            // device tabs used to write into a bag nothing read.
            this.form.setScrollEffect(background.scrollEffect);
        });

        if (image) {
            this.siteFacade.resizeImage({
                path: image.path,
                url: `${environment.fileManagerCdnUrl}${image.url}`,
            });
        }
    }

    public showEditImageModal(): void {
        this.imageEditorService.showEditBackgroundImageModal(
            this.backgroundImage() as SelectedFileExtended,
            this.entity(),
            WidgetDataSubTypes.column,
            this.device()
        );
    }

    public openUnsplashModal(): void {
        this.useUnsplash.openUnsplashModal({
            dataType: WidgetsDataTypes.backgroundImage,
            dataSubType: WidgetDataSubTypes.column,
            entity: this.entity(),
            device: this.device(),
        });
    }

    private writeBorderSettings(settings: AdvancedBorderSettings, device: Device): void {
        const form = this.form;

        form.setBorderEnabled(Boolean(settings.isActive), device);
        this.rememberIndependentBorders(device, Boolean(settings.isIndependentBorders));
        SIDES.forEach((side) => form.setBorderSide(side, fromBorderSettings(settings, side), device));
    }

    private borderSettingsFor(device: Device): AdvancedBorderSettings {
        const border = this.props()?.border;

        const sides = SIDES.flatMap((side) => {
            const [widthKey, styleKey, colorKey] = SIDE_KEY[side];
            // A contour that is off stores no sides; show the ones switching it on would seed.
            const contour = at(border?.[side], device) ?? createBorderSide();

            return [
                [widthKey, contour?.width],
                [styleKey, contour?.style],
                [colorKey, contour?.color],
            ];
        });

        return {
            isActive: Boolean(at(border?.isEnabled, device)),
            // Derived from the sides, with the switch's own answer winning while
            // the panel is open — see `resolveIndependentBorders`.
            isIndependentBorders: this.resolveIndependentBorders(device, isBorderIndependent(border, device)),
            ...Object.fromEntries(sides),
        } as AdvancedBorderSettings;
    }

    // --- Constants ------------------------------------------------------------

    public readonly BORDER_RADIUS_MIN = CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    public readonly BORDER_RADIUS_MAX = CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    public readonly COLUMN_GAP_MIN = CSS_RESTRICTIONS.COLUMN_INDENT_MIN;
    public readonly COLUMN_GAP_MAX = CSS_RESTRICTIONS.COLUMN_INDENT_MAX;
    public readonly SIZE_MIN = CSS_RESTRICTIONS.SIZE_MIN;
    public readonly SIZE_MAX = CSS_RESTRICTIONS.SIZE_MAX;
}
